import { useId, type MutableRefObject } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Divider } from "@/components/Divider";
import { useCityWithExternalState } from "@/hooks/useCity";
import { PREFECTURES, getPrefectureIdByName } from "@/utils/prefecture";
import {
  commercialStatusOptions,
  corporateKinds,
  listingOptions,
  INDUSTRY_MAJOR_CODES,
  industryLabelByCode,
  PUBLIC_REGISTRY_LABEL,
} from "../constants";
import { useCorporateMasterRegistryTags } from "../hooks/useCorporateMasterRegistryTags";
import { useCorporateMasterSuggest } from "../hooks/useCorporateMasterSuggest";
import { HintIcon } from "./HintIcon";
import { type CorporateMasterSearchConditions } from "./corporateMasterSearchSchema";

const prefectureNames = PREFECTURES.map((pref) => pref.name);

// 説明が無いと意味の分からない欄の説明。欄の下には出さず、「？」のツールチップに入れる（入力の誤りだけ欄の下に出す）。
// 中の仕組み（どのデータを見るか・照合の細かい決まり）は書かず、利用者が知らないと困ることだけを書く。
const hints = {
  corporateNumber: "会社法人等番号（12桁）でも探せます。",
  registryOfficerName: "R.E.DATA Plus で取得された商業登記から探します。",
  sales: "最新の決算期の売上高で絞り込みます。",
  excludeAmbiguousRegistryTag: "法人名だけで紐付けたタグを除きます。",
} as const;

const ranges = [
  ["capitalMin", "capitalMax", "資本金", "円", undefined],
  ["employeesMin", "employeesMax", "従業員数", "人", undefined],
  ["yearFrom", "yearTo", "設立年", "年", undefined],
  ["foundingYearFrom", "foundingYearTo", "創業年", "年", undefined],
  ["salesMin", "salesMax", "売上高", "円", hints.sales],
] as const;

interface Props {
  // PCのサイドパネルとSPのモーダルで同じ入力状態を共有するため、フォームは親で持つ。
  form: UseFormReturn<CorporateMasterSearchConditions>;
  // 候補選択で自動入力した法人番号。法人名を打ち直したら外す（手入力の番号は残す）。
  // SPのモーダルを閉じると本コンポーネントはアンマウントされるため、フォームと同じ親で持つ。
  suggestedNumberRef: MutableRefObject<string | null>;
  onSearch: (values: CorporateMasterSearchConditions) => void;
  onClear: () => void;
}

/**
 * 法人マスタの検索条件フォーム。
 * 入力は既存の C 接頭辞コンポーネント（CTextField/CMultiSelect 等）ではなく MUI を直接使う。
 * CTextField は入力の誤りを欄の下（helperText）に出せず、CMultiSelect は Select 表示で Autocomplete
 * （入力で候補を絞る・チップ表示）と見た目が変わるため、現行の見た目を保つ目的でこの形にしている。
 * 説明が無いと意味の分からない欄にだけ、HintIcon（「？」のツールチップ）を置く。
 */
export const CorporateMasterSearchCondition: React.FC<Props> = ({
  form,
  suggestedNumberRef: suggestedNumber,
  onSearch,
  onClear,
}) => {
  const {
    register,
    control,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = form;
  const selectedPrefectures = watch("prefecture");
  const selectedRegistryTags = watch("registryTag");
  // 退任した人を含める指定は代表者名・役員名にだけ効くため、どちらも空なら押せなくする
  const hasPersonName = Boolean(
    watch("representative").trim() || watch("registryOfficerName").trim()
  );
  const registryGroupHeadingId = useId();
  const { data: registryTagOptions } = useCorporateMasterRegistryTags();
  const registryTags = registryTagOptions?.results ?? [];
  const nameInput = watch("name");
  const { data: suggestions, isLoading: isSuggestLoading } =
    useCorporateMasterSuggest(nameInput);
  // 市区町村候補は選択中の都道府県から既存の市区町村API（GET /general/city）で取得する。
  const prefCodes = selectedPrefectures
    .map((name) => String(getPrefectureIdByName(name) ?? ""))
    .filter((code) => code !== "");
  const { cityList } = useCityWithExternalState(prefCodes);
  const cityOptions = (cityList?.list ?? []).map((city) => city.name);

  return (
    <Box
      component="form"
      sx={{ p: 2 }}
      aria-label="法人情報検索"
      noValidate
      onSubmit={(event) => {
        void handleSubmit(onSearch)(event);
      }}
    >
      <Grid container spacing={2} sx={{ pb: 2 }}>
        <Grid item xs={12}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Autocomplete
                freeSolo
                options={suggestions?.results ?? []}
                // 候補はBEで表記ゆれ（かな/カナ・全半角）込みで絞り込み済み。
                // MUI既定の部分一致フィルタで再度絞ると「すた」→「スターツ」等が消えるため無効化する。
                filterOptions={(options) => options}
                loading={isSuggestLoading}
                inputValue={field.value}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name ?? ""
                }
                onInputChange={(_event, value, reason) => {
                  if (reason !== "input" && reason !== "clear") return;
                  field.onChange(value);
                  if (
                    suggestedNumber.current !== null &&
                    getValues("corporateNumber") === suggestedNumber.current
                  )
                    setValue("corporateNumber", "");
                  suggestedNumber.current = null;
                }}
                onChange={(_event, value) => {
                  if (typeof value === "string") field.onChange(value);
                  else if (value) {
                    field.onChange(value.name ?? "");
                    setValue("corporateNumber", value.corporateNumber);
                    suggestedNumber.current = value.corporateNumber;
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.corporateNumber}>
                    {option.name}{" "}
                    {[option.prefectureName, option.cityName]
                      .filter(Boolean)
                      .join("")}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="法人名" />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="法人番号"
            inputProps={{ inputMode: "numeric" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <HintIcon label="法人番号" hint={hints.corporateNumber} />
                </InputAdornment>
              ),
            }}
            // サジェスト選択でsetValueした際もラベルが値に重ならないよう、値があれば縮める。
            InputLabelProps={{
              shrink: Boolean(watch("corporateNumber")) || undefined,
            }}
            error={Boolean(errors.corporateNumber)}
            helperText={errors.corporateNumber?.message}
            {...register("corporateNumber")}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="prefecture"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={prefectureNames}
                value={field.value}
                onChange={(_event, value) => {
                  field.onChange(value);
                  // 選択解除された都道府県配下の市区町村は選択から外す。
                  setValue("city", []);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="都道府県" />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                freeSolo
                disabled={selectedPrefectures.length === 0}
                options={cityOptions}
                value={field.value}
                onChange={(_event, value) => {
                  field.onChange(value);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="市区町村" />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="registryTag"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={registryTags}
                getOptionLabel={(option) => option.label}
                // 選択肢を読めないとき・選択肢から消えたコードも、URL の条件として見せて外せるようにする
                value={field.value.map(
                  (code) =>
                    registryTags.find((o) => o.code === code) ?? {
                      code,
                      label: code,
                    }
                )}
                onChange={(_event, selected) => {
                  field.onChange(selected.map((o) => o.code));
                }}
                isOptionEqualToValue={(o, v) => o.code === v.code}
                renderInput={(params) => (
                  <TextField {...params} label={PUBLIC_REGISTRY_LABEL} />
                )}
              />
            )}
          />
          <Stack direction="row" sx={{ alignItems: "center" }}>
            <Controller
              name="excludeAmbiguousRegistryTag"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  label="曖昧な紐付けを除く"
                  disabled={!selectedRegistryTags.length}
                  control={
                    <Checkbox
                      size="small"
                      checked={field.value}
                      onChange={(event) => {
                        field.onChange(event.target.checked);
                      }}
                    />
                  }
                />
              )}
            />
            <HintIcon
              label="曖昧な紐付けを除く"
              hint={hints.excludeAmbiguousRegistryTag}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="industry"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={INDUSTRY_MAJOR_CODES}
                getOptionLabel={(code) => industryLabelByCode[code]}
                value={field.value}
                onChange={(_event, selected) => {
                  field.onChange(selected);
                }}
                renderInput={(params) => <TextField {...params} label="業種" />}
              />
            )}
          />
        </Grid>
      </Grid>
      <Box sx={{ mx: 0, mb: 2 }}>
        <Divider />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            label="上場区分"
            defaultValue=""
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            {...register("listing")}
          >
            <option value="">すべて</option>
            {listingOptions.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="kind"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={corporateKinds}
                value={field.value}
                onChange={(_event, selected) => {
                  field.onChange(selected);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="法人種別" />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="代表者名"
            error={Boolean(errors.representative)}
            helperText={errors.representative?.message}
            {...register("representative")}
          />
        </Grid>
        <Grid item xs={12}>
          {/* 商業登記だけで引く条件は、欄名の括弧書きや注記ではなく「商業登記」のまとまりで示す */}
          <Stack
            component="section"
            aria-labelledby={registryGroupHeadingId}
            spacing={2}
          >
            <Typography
              id={registryGroupHeadingId}
              component="h3"
              variant="subtitle2"
              sx={{ fontWeight: "bold" }}
            >
              商業登記
            </Typography>
            <Controller
              name="commercialStatus"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={commercialStatusOptions}
                  getOptionLabel={(option) => option.label}
                  value={commercialStatusOptions.filter((o) =>
                    field.value.includes(o.value)
                  )}
                  onChange={(_event, selected) => {
                    field.onChange(selected.map((o) => o.value));
                  }}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  renderInput={(params) => (
                    <TextField {...params} label="商業登記取得状況" />
                  )}
                />
              )}
            />
            <TextField
              fullWidth
              label="役員名"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <HintIcon label="役員名" hint={hints.registryOfficerName} />
                  </InputAdornment>
                ),
              }}
              error={Boolean(errors.registryOfficerName)}
              helperText={errors.registryOfficerName?.message}
              {...register("registryOfficerName")}
            />
            <Controller
              name="includeFormerOfficers"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  label="退任した役員も含める"
                  disabled={!hasPersonName}
                  control={
                    <Checkbox
                      size="small"
                      checked={field.value}
                      onChange={(event) => {
                        field.onChange(event.target.checked);
                      }}
                    />
                  }
                />
              )}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        {ranges.map(([min, max, label, unit, hint]) => (
          <Grid item xs={12} key={min}>
            {/* 320pxのサイドパネルでラベルが切れないよう下限/上限は縦積みにする。 */}
            <Stack direction="column" spacing={1}>
              {[min, max].map((field, index) => (
                <TextField
                  key={field}
                  fullWidth
                  type="number"
                  label={`${label}（${
                    index === 0 ? "下限" : "上限"
                  }・${unit}）`}
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={
                    hint && index === 0
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <HintIcon label={label} hint={hint} />
                            </InputAdornment>
                          ),
                        }
                      : undefined
                  }
                  error={Boolean(errors[field])}
                  helperText={errors[field]?.message}
                  {...register(field)}
                />
              ))}
            </Stack>
          </Grid>
        ))}
      </Grid>
      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 2, justifyContent: "flex-end" }}
      >
        <Button type="button" color="inherit" onClick={onClear}>
          クリア
        </Button>
        <Button type="submit" variant="contained">
          検索
        </Button>
      </Stack>
    </Box>
  );
};
